import logger from "@viz-ui/services/logger/logger";
import QueryClient from "@viz-ui/services/query/queryClient/queryClient";
import QueryGenerator from "@viz-ui/services/query/queryGenerator/queryGenerator";
import QueryErrorInterpreter from "@viz-ui/services/query/queryErrorInterpreter/queryErrorInterpreter.service";

/**
 * Chart Data Query Backend Service with Feature Flipper
 * 
 * This service can switch between the original Angular implementation
 * and the new viz-ui implementation using a feature flipper.
 * 
 * Configuration:
 * - AppConfig.features.migrateChartDataQueryService = true  → Use viz-ui implementation
 * - AppConfig.features.migrateChartDataQueryService = false → Use original Angular implementation
 * - Default (undefined) → Use original Angular implementation
 */
angular
  .module("acl.visualizer.charts.backend")
  .service("ChartDataQueryBackendService", function(
    ChartDataAdapter,
    FieldFormatAdapter,
    DataFilter,
    Sorter,
    SummaryTableAdapter,
    AppConfig
  ) {
    
    // Feature flipper: Check if migrated viz-ui implementation should be used
    const useMigratedImplementation = AppConfig.features && AppConfig.features.migrateChartDataQueryService;
    
    if (useMigratedImplementation) {
      try {
        // Import viz-ui implementation
        const { ChartDataQueryBackendService } = require("@viz-ui/services/charts/chartDataQueryBackend");
        
        // Create instance of viz-ui service with Angular AppConfig
        const vizUiService = new ChartDataQueryBackendService(AppConfig);
        
        // Return interface that matches the original Angular service
        return {
          chartDataRequest: (table, chartType, config, dataConfig, globalFilters, scope, displayConfig) => {
            return vizUiService.chartDataRequest(table, chartType, config, dataConfig, globalFilters, scope, displayConfig);
          }
        };
        
      } catch (error) {
        // Fallback to original implementation if viz-ui service fails to load
        logger.log("⚠️ Failed to load viz-ui ChartDataQueryBackendService, falling back to Angular implementation:", error.message);
      }
    }
    
    // Original Angular implementation (preserved exactly as it was)
    return {
      chartDataRequest: (
        table,
        chartType,
        { filterConfig, formatting },
        dataConfig,
        globalFilters,
        scope,
        displayConfig
      ) => {
        let { filterList } = DataFilter.getFilterRequestBody(filterConfig);

        let aggColumnQuery = QueryGenerator.chartData({
          tableId: table.id(),
          chartType,
          filterList,
          dataConfig,
          globalFilters,
          scope,
        });

        return new Promise((resolve, reject) => {
          QueryClient.query(aggColumnQuery)
            .then(response => {
              const chartData = deserializeChartDataModel(
                chartType,
                dataConfig,
                table,
                formatting,
                response,
                displayConfig
              );
              resolve(chartData);
            })
            .catch(response => {
              const errorCode = QueryErrorInterpreter.errorCode(response);
              reject(errorCode);
            });
        });
      },
    };

    function deserializeChartDataModel(chartType, dataConfig, table, formatting, response, displayConfig) {
      if (chartType === "SummaryTable") {
        const fieldFormatMap = FieldFormatAdapter.deserializeFieldFormatMap(formatting);
        const json = sortSummaryTableData(dataConfig, response.data.table);
        const showTotals = displayConfig.showTotals === false ? false : true;

        return SummaryTableAdapter.deserializeSummaryTable(dataConfig, fieldFormatMap, json, showTotals);
      }
      const aggregationData = response.data.table;
      return ChartDataAdapter.deserializeChartData(chartType, table, dataConfig, aggregationData);
    }

    function sortSummaryTableData(dataConfig, json) {
      let chartColumn = dataConfig.chartColumns.length ? dataConfig.chartColumns[0] : undefined;
      let subtotalData = chartColumn
        ? Sorter.sort(json.subtotalData, {
            dataType: chartColumn.type,
            valueParser: item => item[chartColumn.fieldName],
          })
        : json.subtotalData;

      let { chartRows } = dataConfig;
      let rowTypes = chartRows.map(row => row.type);
      let summarizedData = json.summarizedData.sort((a, b) => {
        let i;
        let result;
        for (i = 0; i < rowTypes.length; i++) {
          let comparator = Sorter.getTypedComparator({ dataType: rowTypes[i] });
          result = comparator(a[chartRows[i].fieldName], b[chartRows[i].fieldName]);
          if (result !== 0) {
            return result;
          }
        }
        return 0;
      });

      return {
        subtotalData: subtotalData,
        summarizedData: summarizedData,
      };
    }
  });
